import { useEffect, useState, useMemo } from 'react'
import styles from './styles.module.scss'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import api from '../../../helpers/apiRequester'
import { UserCreateForm } from './UserCreateForm'
import { UserDetailModal } from './UserDetailModal'

export interface CognitoUser {
    username: string
    // Cognito `sub` (UUID) — required for the additional org memberships
    // table since that's keyed on `cognito_id`, not the Cognito username
    // (which on this pool is often equal to the email).
    cognitoId: string
    email: string
    name: string
    organisation: string
    emailVerified: boolean
    enabled: boolean
    status: string
    createdAt: string
    lastModified: string
    admin: boolean
    // Last sign-in timestamp, stamped server-side on the `/init` bootstrap call.
    // Null when the user has never logged in since tracking was introduced.
    lastLogin: string | null
}

type SortField = 'email' | 'name' | 'organisation' | 'status' | 'createdAt' | 'lastLogin'
type SortDirection = 'asc' | 'desc'
type OrgSortField = 'organisation' | 'total' | 'enabled' | 'disabled' | 'admins'

interface OrgStat {
    organisation: string
    total: number
    enabled: number
    disabled: number
    admins: number
}

// Users with no Cognito `custom:organisation` are grouped under this key.
const NO_ORG = ''
const orgLabel = (org: string) => org || '(No organisation)'

export interface UsersListViewProps {
    // Retained for backwards compatibility with the container; the view now
    // manages its own titles (Organisations overview / per-org drill-down).
    dataViewTitle?: string
}

export const UsersListView = (_props: UsersListViewProps) => {
    const [users, setUsers] = useState<CognitoUser[]>([])
    const [loading, setLoading] = useState(true)
    // Which organisation is being drilled into. `null` shows the overview.
    const [selectedOrg, setSelectedOrg] = useState<string | null>(null)
    const [orgSearch, setOrgSearch] = useState('')
    const [orgSortField, setOrgSortField] = useState<OrgSortField>('total')
    const [orgSortDirection, setOrgSortDirection] = useState<SortDirection>('desc')
    // Search + filters within the drilled-into org's user table.
    const [searchTerm, setSearchTerm] = useState('')
    const [sortField, setSortField] = useState<SortField>('createdAt')
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
    const [filterStatus, setFilterStatus] = useState<string>('')
    const [isSyncing, setIsSyncing] = useState(false)
    const [selectedUser, setSelectedUser] = useState<CognitoUser | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    // Bulk selection — tracked by Cognito `username` (the unique row key).
    const [selectedUsernames, setSelectedUsernames] = useState<Set<string>>(new Set())
    const [isBulkProcessing, setIsBulkProcessing] = useState(false)
    const [isDisablingOrg, setIsDisablingOrg] = useState(false)

    useEffect(() => {
        loadUsers()
    }, [])

    const loadUsers = async () => {
        try {
            setSelectedUsernames(new Set())
            Loading.circle('Loading users...')
            const apiResult = await api({
                url: 'cognito-users-all',
                data: {},
            })

            if (apiResult.data.success) {
                setUsers(apiResult.data.users)
            } else {
                Report.failure('Error', 'Failed to load users. Please try again.', 'Okay')
            }
        } catch (error: any) {
            console.error('Error loading users:', error)
            Report.failure('Error', 'Failed to load users. Please try again.', 'Okay')
        } finally {
            Loading.remove()
            setLoading(false)
        }
    }

    const handleUserCreated = () => {
        loadUsers()
    }

    const handleSyncUsers = async () => {
        Confirm.show(
            'Sync Users',
            'This will sync all Cognito users to the database. This may take a moment. Continue?',
            'Yes, Sync',
            'Cancel',
            async () => {
                try {
                    setIsSyncing(true)
                    Loading.circle('Syncing users to database...')

                    const response = await api({
                        url: 'cognito-users-sync',
                        data: {},
                    })

                    Loading.remove()

                    if (response.data.success) {
                        Report.success(
                            'Sync Complete',
                            `Successfully synced ${response.data.synced} users.\n\nCreated: ${response.data.created}\nUpdated: ${response.data.updated}${response.data.errors?.length > 0 ? `\nErrors: ${response.data.errors.length}` : ''}`,
                            'Okay',
                            () => loadUsers(),
                        )
                    } else {
                        Report.failure('Sync Failed', response.data.error || 'Failed to sync users.', 'Okay')
                    }
                } catch (error: any) {
                    Loading.remove()
                    console.error('Error syncing users:', error)
                    Report.failure('Sync Failed', error.message || 'Failed to sync users.', 'Okay')
                } finally {
                    setIsSyncing(false)
                }
            },
        )
    }

    const handleViewUser = (user: CognitoUser) => {
        setSelectedUser(user)
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedUser(null)
    }

    const handleUserUpdated = () => {
        loadUsers()
        // Update selected user if modal is still open
        setIsModalOpen(false)
        setSelectedUser(null)
    }

    // ─── Org navigation ─────────────────────────────────────────────────────
    const handleViewOrg = (organisation: string) => {
        setSelectedOrg(organisation)
        setSelectedUsernames(new Set())
        setSearchTerm('')
        setFilterStatus('')
    }

    const handleBackToOrgs = () => {
        setSelectedOrg(null)
        setSelectedUsernames(new Set())
        setSearchTerm('')
        setFilterStatus('')
    }

    // ─── Bulk selection helpers ─────────────────────────────────────────────
    const toggleSelectUser = (username: string) => {
        setSelectedUsernames((prev) => {
            const next = new Set(prev)
            if (next.has(username)) {
                next.delete(username)
            } else {
                next.add(username)
            }
            return next
        })
    }

    const clearSelection = () => setSelectedUsernames(new Set())

    /**
     * Run an async worker over a list with a small concurrency cap so we don't
     * hammer Cognito (which throttles aggressively) when acting on many users.
     * The backend per-user endpoints return non-2xx on failure (e.g. the
     * self-delete guard), so a throw counts as a failure.
     */
    const runWithConcurrency = async <T,>(
        items: T[],
        worker: (item: T) => Promise<boolean>,
        limit = 4,
    ): Promise<{ success: number; failure: number }> => {
        let success = 0
        let failure = 0
        let cursor = 0

        const runner = async () => {
            while (cursor < items.length) {
                const item = items[cursor++]
                try {
                    const ok = await worker(item)
                    ok ? success++ : failure++
                } catch {
                    failure++
                }
            }
        }

        await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runner))
        return { success, failure }
    }

    const reportBulkResult = (title: string, success: number, failure: number, onDone?: () => void) => {
        const noun = (n: number) => `${n} user${n === 1 ? '' : 's'}`
        if (failure === 0) {
            Report.success(title, `${noun(success)} processed successfully.`, 'Okay', onDone)
        } else if (success === 0) {
            Report.failure(title, `Failed to process ${noun(failure)}.`, 'Okay', onDone)
        } else {
            Report.warning(title, `${noun(success)} succeeded, ${failure} failed.`, 'Okay', onDone)
        }
    }

    const handleBulkStatus = (enable: boolean) => {
        const selectedUsers = users.filter((u) => selectedUsernames.has(u.username))
        if (selectedUsers.length === 0) return

        const action = enable ? 'enable' : 'disable'
        // Skip users already in the target state to avoid pointless API calls
        // and to keep the success/failure counts meaningful.
        const targets = selectedUsers.filter((u) => (enable ? !u.enabled : u.enabled))

        if (targets.length === 0) {
            Report.info('No Changes', `All selected users are already ${enable ? 'enabled' : 'disabled'}.`, 'Okay')
            return
        }

        Confirm.show(
            `${enable ? 'Enable' : 'Disable'} Users`,
            `Are you sure you want to ${action} ${targets.length} user${targets.length === 1 ? '' : 's'}?`,
            'Yes',
            'Cancel',
            async () => {
                setIsBulkProcessing(true)
                Loading.circle(`${enable ? 'Enabling' : 'Disabling'} users...`)
                try {
                    const { success, failure } = await runWithConcurrency(targets, async (u) => {
                        const res = await api({
                            url: 'cognito-users-update-status',
                            data: { email: u.email, enabled: enable },
                        })
                        return !!res.data?.success
                    })
                    Loading.remove()
                    reportBulkResult(`${enable ? 'Enable' : 'Disable'} Complete`, success, failure, () => loadUsers())
                } catch (error: any) {
                    Loading.remove()
                    console.error(`Error during bulk ${action}:`, error)
                    Report.failure('Error', `Failed to ${action} users. Please try again.`, 'Okay')
                } finally {
                    setIsBulkProcessing(false)
                }
            },
        )
    }

    const handleBulkDelete = () => {
        const selectedUsers = users.filter((u) => selectedUsernames.has(u.username))
        if (selectedUsers.length === 0) return

        const count = selectedUsers.length

        Confirm.show(
            'Delete Users',
            `This will permanently delete ${count} user${count === 1 ? '' : 's'} from Cognito and the dashboard. This cannot be undone. Are you sure?`,
            'Yes, Delete',
            'Cancel',
            async () => {
                setIsBulkProcessing(true)
                Loading.circle('Deleting users...')
                try {
                    const { success, failure } = await runWithConcurrency(
                        selectedUsers,
                        async (u) => {
                            const res = await api({
                                url: 'cognito-users-delete',
                                data: { email: u.email, cognitoId: u.cognitoId },
                            })
                            return !!res.data?.success
                        },
                        3,
                    )
                    Loading.remove()
                    reportBulkResult('Delete Complete', success, failure, () => loadUsers())
                } catch (error: any) {
                    Loading.remove()
                    console.error('Error during bulk delete:', error)
                    Report.failure('Error', 'Failed to delete users. Please try again.', 'Okay')
                } finally {
                    setIsBulkProcessing(false)
                }
            },
        )
    }

    const handleDisableOrganisation = (organisation: string) => {
        if (!organisation) {
            Report.info(
                'Not Available',
                'Users with no organisation cannot be disabled as a group. Disable them individually or in bulk.',
                'Okay',
            )
            return
        }

        const sentinelWarning =
            organisation === 'Sentinel'
                ? '\n\nWARNING: Sentinel includes admin users — this may disable other administrators.'
                : ''

        Confirm.show(
            'Disable Organisation Access',
            `This will disable login and revoke active sessions for ALL users in "${organisation}" ` +
                `(including users who have it as an additional organisation), and remove those additional ` +
                `memberships. Your own account is not affected.${sentinelWarning}\n\nAre you sure?`,
            'Yes, Disable',
            'Cancel',
            async () => {
                setIsDisablingOrg(true)
                Loading.circle(`Disabling access for ${organisation}...`)
                try {
                    const response = await api({
                        url: 'cognito-users-disable-organisation',
                        data: { organisation },
                    })

                    Loading.remove()

                    if (response.data.success) {
                        const { disabled = 0, revokedMemberships = 0, failures = [] } = response.data
                        const failureNote =
                            failures.length > 0 ? `\n\nFailed to disable ${failures.length} user(s).` : ''
                        const title = failures.length > 0 ? 'Completed with Errors' : 'Access Disabled'
                        const report = failures.length > 0 ? Report.warning : Report.success
                        report(
                            title,
                            `${disabled} user${disabled === 1 ? '' : 's'} disabled and signed out.\n` +
                                `${revokedMemberships} additional membership${
                                    revokedMemberships === 1 ? '' : 's'
                                } revoked.${failureNote}`,
                            'Okay',
                            () => loadUsers(),
                        )
                    } else {
                        Report.failure(
                            'Failed',
                            response.data.error || 'Failed to disable organisation access.',
                            'Okay',
                        )
                    }
                } catch (error: any) {
                    Loading.remove()
                    console.error('Error disabling organisation access:', error)
                    Report.failure('Error', 'Failed to disable organisation access. Please try again.', 'Okay')
                } finally {
                    setIsDisablingOrg(false)
                }
            },
        )
    }

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return '↕'
        return sortDirection === 'asc' ? '↑' : '↓'
    }

    const handleOrgSort = (field: OrgSortField) => {
        if (orgSortField === field) {
            setOrgSortDirection(orgSortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setOrgSortField(field)
            setOrgSortDirection(field === 'organisation' ? 'asc' : 'desc')
        }
    }

    const getOrgSortIcon = (field: OrgSortField) => {
        if (orgSortField !== field) return '↕'
        return orgSortDirection === 'asc' ? '↑' : '↓'
    }

    // ─── Organisation stats (overview) ──────────────────────────────────────
    const orgStats = useMemo<OrgStat[]>(() => {
        const map = new Map<string, OrgStat>()
        users.forEach((u) => {
            const key = u.organisation || NO_ORG
            const stat = map.get(key) || { organisation: key, total: 0, enabled: 0, disabled: 0, admins: 0 }
            stat.total++
            if (u.enabled) stat.enabled++
            else stat.disabled++
            if (u.admin) stat.admins++
            map.set(key, stat)
        })
        return Array.from(map.values())
    }, [users])

    const filteredAndSortedOrgs = useMemo(() => {
        let result = [...orgStats]

        if (orgSearch.trim()) {
            const term = orgSearch.trim().toLowerCase()
            result = result.filter((o) => orgLabel(o.organisation).toLowerCase().includes(term))
        }

        result.sort((a, b) => {
            let aVal: string | number
            let bVal: string | number
            if (orgSortField === 'organisation') {
                aVal = orgLabel(a.organisation).toLowerCase()
                bVal = orgLabel(b.organisation).toLowerCase()
            } else {
                aVal = a[orgSortField]
                bVal = b[orgSortField]
            }
            if (aVal < bVal) return orgSortDirection === 'asc' ? -1 : 1
            if (aVal > bVal) return orgSortDirection === 'asc' ? 1 : -1
            return 0
        })

        return result
    }, [orgStats, orgSearch, orgSortField, orgSortDirection])

    // ─── Users of the selected org (drill-down) ─────────────────────────────
    const orgUsers = useMemo(() => {
        if (selectedOrg === null) return []
        return users.filter((u) => (u.organisation || NO_ORG) === selectedOrg)
    }, [users, selectedOrg])

    const selectedOrgStat = useMemo<OrgStat>(() => {
        const base: OrgStat = {
            organisation: selectedOrg || NO_ORG,
            total: 0,
            enabled: 0,
            disabled: 0,
            admins: 0,
        }
        orgUsers.forEach((u) => {
            base.total++
            if (u.enabled) base.enabled++
            else base.disabled++
            if (u.admin) base.admins++
        })
        return base
    }, [orgUsers, selectedOrg])

    const filteredAndSortedUsers = useMemo(() => {
        let result = [...orgUsers]

        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase()
            result = result.filter(
                (u) =>
                    u.email.toLowerCase().includes(term) || (u.name || '').toLowerCase().includes(term),
            )
        }

        // Apply status filter
        if (filterStatus) {
            if (filterStatus === 'enabled') {
                result = result.filter((u) => u.enabled)
            } else if (filterStatus === 'disabled') {
                result = result.filter((u) => !u.enabled)
            } else if (filterStatus === 'confirmed') {
                result = result.filter((u) => u.status === 'CONFIRMED')
            } else if (filterStatus === 'pending') {
                result = result.filter((u) => u.status === 'FORCE_CHANGE_PASSWORD')
            }
        }

        // Apply sorting
        result.sort((a, b) => {
            let aVal: string | number = ''
            let bVal: string | number = ''

            switch (sortField) {
                case 'email':
                    aVal = a.email.toLowerCase()
                    bVal = b.email.toLowerCase()
                    break
                case 'name':
                    aVal = (a.name || '').toLowerCase()
                    bVal = (b.name || '').toLowerCase()
                    break
                case 'organisation':
                    aVal = (a.organisation || '').toLowerCase()
                    bVal = (b.organisation || '').toLowerCase()
                    break
                case 'status':
                    aVal = a.enabled ? 'enabled' : 'disabled'
                    bVal = b.enabled ? 'enabled' : 'disabled'
                    break
                case 'createdAt':
                    aVal = new Date(a.createdAt).getTime()
                    bVal = new Date(b.createdAt).getTime()
                    break
                case 'lastLogin':
                    // Never-logged-in users sort as the oldest (0).
                    aVal = a.lastLogin ? new Date(a.lastLogin).getTime() : 0
                    bVal = b.lastLogin ? new Date(b.lastLogin).getTime() : 0
                    break
            }

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
            return 0
        })

        return result
    }, [orgUsers, searchTerm, filterStatus, sortField, sortDirection])

    const formatDate = (dateString: string) => {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    }

    const formatLastLogin = (dateString: string | null) => {
        if (!dateString) return 'Never'
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const getStatusBadge = (status: string, enabled: boolean) => {
        if (!enabled) {
            return <span className={`${styles.badge} ${styles.disabled}`}>Disabled</span>
        }
        switch (status) {
            case 'CONFIRMED':
                return <span className={`${styles.badge} ${styles.confirmed}`}>Active</span>
            case 'UNCONFIRMED':
                return <span className={`${styles.badge} ${styles.unconfirmed}`}>Unconfirmed</span>
            case 'FORCE_CHANGE_PASSWORD':
                return <span className={`${styles.badge} ${styles.pending}`}>Pending</span>
            default:
                return <span className={styles.badge}>{status}</span>
        }
    }

    // Derived selection state for the current (filtered) view.
    const selectedCount = filteredAndSortedUsers.filter((u) => selectedUsernames.has(u.username)).length
    const allVisibleSelected = filteredAndSortedUsers.length > 0 && selectedCount === filteredAndSortedUsers.length
    const someVisibleSelected = selectedCount > 0 && !allVisibleSelected

    const toggleSelectAll = () => {
        setSelectedUsernames((prev) => {
            const next = new Set(prev)
            if (allVisibleSelected) {
                filteredAndSortedUsers.forEach((u) => next.delete(u.username))
            } else {
                filteredAndSortedUsers.forEach((u) => next.add(u.username))
            }
            return next
        })
    }

    if (loading) {
        return (
            <div className={styles.usersContainer}>
                <div className={styles.resultsContainer}>
                    <div className={styles.loadingState}>
                        <p>Loading users...</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.usersContainer}>
            {/* Create User Section */}
            <UserCreateForm onUserCreated={handleUserCreated} onSyncUsers={handleSyncUsers} isSyncing={isSyncing} />

            {selectedOrg === null ? (
                /* ─── Organisations Overview ─────────────────────────────── */
                <div className={styles.resultsContainer}>
                    <div className={styles.resultsHeader}>
                        <h3>Organisations</h3>
                        <div className={styles.headerActions}>
                            <div className={styles.searchWrapper}>
                                <input
                                    className={styles.searchInput}
                                    type="search"
                                    value={orgSearch}
                                    onChange={(e) => setOrgSearch(e.target.value)}
                                    placeholder="Search organisations..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.tableWrapper}>
                        <table className={styles.resultsTable}>
                            <thead>
                                <tr>
                                    <th
                                        className={`${styles.sortable} ${orgSortField === 'organisation' ? styles.sorted : ''}`}
                                        onClick={() => handleOrgSort('organisation')}
                                    >
                                        Organisation
                                        <span className={styles.sortIcon}>{getOrgSortIcon('organisation')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${orgSortField === 'total' ? styles.sorted : ''}`}
                                        onClick={() => handleOrgSort('total')}
                                    >
                                        Users
                                        <span className={styles.sortIcon}>{getOrgSortIcon('total')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${orgSortField === 'enabled' ? styles.sorted : ''}`}
                                        onClick={() => handleOrgSort('enabled')}
                                    >
                                        Enabled
                                        <span className={styles.sortIcon}>{getOrgSortIcon('enabled')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${orgSortField === 'disabled' ? styles.sorted : ''}`}
                                        onClick={() => handleOrgSort('disabled')}
                                    >
                                        Disabled
                                        <span className={styles.sortIcon}>{getOrgSortIcon('disabled')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${orgSortField === 'admins' ? styles.sorted : ''}`}
                                        onClick={() => handleOrgSort('admins')}
                                    >
                                        Admins
                                        <span className={styles.sortIcon}>{getOrgSortIcon('admins')}</span>
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedOrgs.length > 0 ? (
                                    filteredAndSortedOrgs.map((org) => (
                                        <tr key={org.organisation || '__none__'}>
                                            <td>
                                                <span className={styles.orgBadge}>{orgLabel(org.organisation)}</span>
                                            </td>
                                            <td>{org.total}</td>
                                            <td>{org.enabled}</td>
                                            <td>{org.disabled}</td>
                                            <td>{org.admins}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleViewOrg(org.organisation)}
                                                    className={styles.viewButton}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className={styles.noResults}>
                                            No organisations found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.tableFooter}>
                        <span className={styles.resultsCount}>
                            {filteredAndSortedOrgs.length} organisation
                            {filteredAndSortedOrgs.length !== 1 ? 's' : ''} · {users.length} user
                            {users.length !== 1 ? 's' : ''} total
                        </span>
                    </div>
                </div>
            ) : (
                /* ─── Organisation Users (drill-down) ────────────────────── */
                <div className={styles.resultsContainer}>
                    <div className={styles.orgUsersHeader}>
                        <button type="button" className={styles.backButton} onClick={handleBackToOrgs}>
                            ← All Organisations
                        </button>
                        <h3>{orgLabel(selectedOrg)}</h3>
                    </div>

                    <div className={styles.summaryCards}>
                        <div className={styles.summaryCard}>
                            <span className={styles.summaryCardValue}>{selectedOrgStat.total}</span>
                            <span className={styles.summaryCardLabel}>Total Users</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.summaryEnabled}`}>
                            <span className={styles.summaryCardValue}>{selectedOrgStat.enabled}</span>
                            <span className={styles.summaryCardLabel}>Enabled</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.summaryDisabled}`}>
                            <span className={styles.summaryCardValue}>{selectedOrgStat.disabled}</span>
                            <span className={styles.summaryCardLabel}>Disabled</span>
                        </div>
                        <div className={styles.summaryCard}>
                            <span className={styles.summaryCardValue}>{selectedOrgStat.admins}</span>
                            <span className={styles.summaryCardLabel}>Admins</span>
                        </div>
                    </div>

                    <div className={styles.resultsHeader}>
                        <h3>Users</h3>
                        <div className={styles.headerActions}>
                            <div className={styles.searchWrapper}>
                                <input
                                    className={styles.searchInput}
                                    type="search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by email or name..."
                                />
                            </div>

                            <div className={styles.filterWrapper}>
                                <select
                                    className={styles.filterSelect}
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="enabled">Enabled</option>
                                    <option value="disabled">Disabled</option>
                                    <option value="confirmed">Active</option>
                                    <option value="pending">Pending Setup</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {selectedOrg !== NO_ORG && (
                        <div className={styles.orgAccessBar}>
                            <span className={styles.orgAccessLabel}>
                                Disable login and revoke sessions for every user in {orgLabel(selectedOrg)}.
                            </span>
                            <button
                                type="button"
                                className={`${styles.bulkButton} ${styles.bulkDisable}`}
                                onClick={() => handleDisableOrganisation(selectedOrg)}
                                disabled={isDisablingOrg}
                            >
                                Disable All Access
                            </button>
                        </div>
                    )}

                    {selectedCount > 0 && (
                        <div className={styles.bulkActionsBar}>
                            <span className={styles.bulkInfo}>{selectedCount} selected</span>
                            <div className={styles.bulkButtons}>
                                <button
                                    type="button"
                                    className={`${styles.bulkButton} ${styles.bulkEnable}`}
                                    onClick={() => handleBulkStatus(true)}
                                    disabled={isBulkProcessing}
                                >
                                    Enable
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.bulkButton} ${styles.bulkDisable}`}
                                    onClick={() => handleBulkStatus(false)}
                                    disabled={isBulkProcessing}
                                >
                                    Disable
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.bulkButton} ${styles.bulkDelete}`}
                                    onClick={handleBulkDelete}
                                    disabled={isBulkProcessing}
                                >
                                    Delete
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.bulkButton} ${styles.bulkClear}`}
                                    onClick={clearSelection}
                                    disabled={isBulkProcessing}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}

                    <div className={styles.tableWrapper}>
                        <table className={styles.resultsTable}>
                            <thead>
                                <tr>
                                    <th className={styles.checkboxCell}>
                                        <input
                                            type="checkbox"
                                            aria-label="Select all users"
                                            checked={allVisibleSelected}
                                            ref={(el) => {
                                                if (el) el.indeterminate = someVisibleSelected
                                            }}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${sortField === 'email' ? styles.sorted : ''}`}
                                        onClick={() => handleSort('email')}
                                    >
                                        Email
                                        <span className={styles.sortIcon}>{getSortIcon('email')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${sortField === 'name' ? styles.sorted : ''}`}
                                        onClick={() => handleSort('name')}
                                    >
                                        Name
                                        <span className={styles.sortIcon}>{getSortIcon('name')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${sortField === 'status' ? styles.sorted : ''}`}
                                        onClick={() => handleSort('status')}
                                    >
                                        Status
                                        <span className={styles.sortIcon}>{getSortIcon('status')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${sortField === 'createdAt' ? styles.sorted : ''}`}
                                        onClick={() => handleSort('createdAt')}
                                    >
                                        Created
                                        <span className={styles.sortIcon}>{getSortIcon('createdAt')}</span>
                                    </th>
                                    <th
                                        className={`${styles.sortable} ${sortField === 'lastLogin' ? styles.sorted : ''}`}
                                        onClick={() => handleSort('lastLogin')}
                                    >
                                        Last Login
                                        <span className={styles.sortIcon}>{getSortIcon('lastLogin')}</span>
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedUsers.length > 0 ? (
                                    filteredAndSortedUsers.map((user) => (
                                        <tr
                                            key={user.username}
                                            className={selectedUsernames.has(user.username) ? styles.rowSelected : ''}
                                        >
                                            <td className={styles.checkboxCell}>
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Select ${user.email}`}
                                                    checked={selectedUsernames.has(user.username)}
                                                    onChange={() => toggleSelectUser(user.username)}
                                                />
                                            </td>
                                            <td>{user.email}</td>
                                            <td>{user.name || '-'}</td>
                                            <td>{getStatusBadge(user.status, user.enabled)}</td>
                                            <td>{formatDate(user.createdAt)}</td>
                                            <td>{formatLastLogin(user.lastLogin)}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleViewUser(user)}
                                                    className={styles.viewButton}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className={styles.noResults}>
                                            No users found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.tableFooter}>
                        <span className={styles.resultsCount}>
                            Showing {filteredAndSortedUsers.length}
                            {filteredAndSortedUsers.length !== orgUsers.length && ` of ${orgUsers.length}`} user
                            {filteredAndSortedUsers.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>
            )}

            {/* User Detail Modal */}
            <UserDetailModal
                user={selectedUser}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onUserUpdated={handleUserUpdated}
            />
        </div>
    )
}

export default UsersListView
